/**
 * Secondary CTA Decision Engine
 *
 * Pure, synchronous. `resolveSecondaryCtas` returns up to two CTAs rendered
 * underneath the primary CTA, given already-fetched supporter data + the
 * resolved primary CTAs.
 *
 * Slots fill top-to-bottom until two CTAs are chosen:
 *   Tax season → Tax receipts
 *   Active pledge → Beyond Measure (with no active sponsorships, returns here —
 *     just Beyond Measure, plus Tax receipts if it's also tax season)
 *   Both slots already filled (tax + pledge) → return
 *   Active commitment but no payment on file → Add payment method
 *   Not a sponsor → active appeal replaces Donate; Volunteer remains
 *   Sponsor → sponsor sub-flow
 *
 * Tax and pledge stack rather than short-circuiting each other; a "No
 * sponsorships" primary is handled by the zero-sponsorship path above, not a
 * special-case short-circuit.
 *
 * Monetary-ask cap: across primary + secondary, at most MAX_MONETARY_ASKS
 * `monetaryAsk` CTAs render; primaries count first, the rest is the budget.
 *
 * Sponsor sub-flow — priority-ordered candidates, then the budget drops any
 * monetary ones that no longer fit:
 *   1. Write a letter (specific children if >60d stale, else generic)
 *   2. Active appeals — except Christmas Gift Catalog when Christmas is primary
 *   3. Send a Gift (monetary)
 *   4. Volunteer — non-monetary fallback for a budget-suppressed slot
 *
 * Time-based branches read the current date internally — no injected clock.
 * Only the result types, input type, and resolver are exported; the decision
 * predicates and builders below are private to this module.
 */
import { differenceInDays } from 'date-fns';

import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import type {
  GraphQLCommitment,
  GraphQLPledge,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import {
  filterActiveSponsorshipCommitments,
  isCommitmentActive,
  isSponsorshipCommitmentGiftable,
  isSponsorshipCommitmentWritable,
} from '@sola/shared-utils/supporters/commitments.ts';
import { isDuringTaxSeason } from '@sola/shared-utils/supporters/financials.ts';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { isMonthDayWithinRange } from '@sola/utils/date.ts';
import { getElapsed } from '@sola/utils/relativeTime.ts';

import {
  christmasGiftCatalogAppealMetadata,
  giveThanksAppealMetadata,
} from '../../organisms/notifications/templates/appeal-metadata.ts';
import type { PrimaryCtaResult } from './primary-cta-decision.ts';
import { PrimaryCtaType } from './primary-cta-decision.ts';

// ─── Types ─────────────────────────────────────────────────────────────────

export enum SecondaryCtaType {
  TaxReceipts = 'tax-receipts',
  BeyondMeasure = 'beyond-measure',
  DonateToCause = 'donate-to-cause',
  Volunteer = 'volunteer',
  AddPaymentMethod = 'add-payment-method',
  WriteLetterSpecific = 'write-letter-specific',
  WriteLetterGeneric = 'write-letter-generic',
  SendGift = 'send-gift',
  GiveThanks = 'give-thanks',
  ChristmasGiftCatalog = 'christmas-gift-catalog',
}

// Rendered as a LinkCard, so it carries i18n keys + interpolation rather than
// resolved strings.
type SecondaryCtaResultBase = {
  type: SecondaryCtaType;
  /** Does this CTA ask the supporter for money? Feeds the monetary-ask cap. */
  monetaryAsk: boolean;
  titleKey: string;
  subtitleKey?: string;
  interpolation?: Record<string, string>;
  iconName: IconNames;
};
type SecondaryCtaResultUrl = {
  hrefType: 'url';
  url: string;
};
type SecondaryCtaResultHrefKey = {
  hrefKey: string;
};

export type SecondaryCtaResult = SecondaryCtaResultBase &
  (SecondaryCtaResultUrl | SecondaryCtaResultHrefKey);

/** The homepage renders up to two secondary CTAs underneath the primary CTA. */
export type SecondaryCtaResolution = SecondaryCtaResult[];

export type SecondaryCtaInput = {
  commitments: GraphQLCommitment[];
  pledges: GraphQLPledge[];
  paymentMethods: GraphQLSupporterPaymentMethod[];
  primaryCtas: PrimaryCtaResult[];
};

const LETTER_STALE_DAYS = 60;

/**
 * Interpolation sentinel for the write-letter CTA. When a sponsorship has no
 * correspondence date, the (pure) engine can't translate, so it emits this key
 * as the `lastWritten` value; the rendering component detects it and resolves
 * the translated fallback ("N/A").
 */
export const lastWrittenFallbackKey =
  'my:homepage.overview.writeCta.lastWritten_fallback';

// ─── Decision predicates ─────────────────────────────────────────────────────

/** Is it tax season? (drives the tax-receipts CTA) */
const isTaxSeason = (): boolean => isDuringTaxSeason();

/** Does the supporter have an active pledge? (drives Beyond Measure) */
const hasActivePledge = (pledges: GraphQLPledge[]): boolean =>
  pledges.some((p) => p.status === CommitmentStatusEnum.Active);

/** Does the supporter have any payment method on file? */
const hasAnyPaymentMethod = (
  paymentMethods: GraphQLSupporterPaymentMethod[],
): boolean => paymentMethods.length > 0;

/** Any active commitment (sponsorship or not) → drives Add payment method. */
const hasActiveCommitment = (commitments: GraphQLCommitment[]): boolean =>
  commitments.some(isCommitmentActive);

/** Is Christmas already represented by a primary CTA? */
const primaryHasChristmas = (primaryCtas: PrimaryCtaResult[]): boolean =>
  primaryCtas.some((cta) => cta.type === PrimaryCtaType.Christmas);

/** Hard cap on monetary "asks" shown across the primary + secondary CTAs. */
const MAX_MONETARY_ASKS = 2;

/** How many of these CTAs ask the supporter for money? */
const countMonetaryAsks = (ctas: readonly { monetaryAsk: boolean }[]): number =>
  ctas.filter((cta) => cta.monetaryAsk).length;

/**
 * Picks up to `limit` candidates in priority order, skipping monetary asks once
 * `monetaryBudget` runs out. Callers put a non-monetary fallback last.
 */
const selectWithinMonetaryBudget = (
  candidates: SecondaryCtaResolution,
  limit: number,
  monetaryBudget: number,
): SecondaryCtaResolution => {
  const selected: SecondaryCtaResolution = [];
  let remainingBudget = monetaryBudget;

  for (const cta of candidates) {
    if (selected.length === limit) {
      break;
    }
    if (cta.monetaryAsk) {
      if (remainingBudget <= 0) {
        continue;
      }
      remainingBudget -= 1;
    }
    selected.push(cta);
  }

  return selected;
};

// ─── Letter helpers ──────────────────────────────────────────────────────────

const isLetterStale = (
  lastCorrespondenceDate: string | null | undefined,
  now: Date,
): boolean => {
  if (!lastCorrespondenceDate) {
    return true;
  }
  return (
    differenceInDays(now, new Date(lastCorrespondenceDate)) > LETTER_STALE_DAYS
  );
};

const getOldestCorrespondenceDate = (
  commitments: GraphQLCommitment[],
): string | null => {
  const dates = commitments
    .map((c) => c.lastCorrespondenceDate)
    .filter((d): d is string => d != null);

  return dates.length === 0 ? null : dates.toSorted()[0];
};

// ─── CTA builders ────────────────────────────────────────────────────────────

const buildTaxReceiptsCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.TaxReceipts,
  monetaryAsk: false,
  hrefKey: 'my:homepage.overview.taxReceiptsCta.url',
  titleKey: 'my:homepage.overview.taxReceiptsCta.title',
  subtitleKey: 'my:homepage.overview.taxReceiptsCta.subtitle',
  iconName: 'receipt-check',
});

const buildBeyondMeasureCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.BeyondMeasure,
  monetaryAsk: false,
  hrefKey: 'my:homepage.overview.pledgeCta.url',
  titleKey: 'my:homepage.overview.pledgeCta.title',
  subtitleKey: 'my:homepage.overview.pledgeCta.subtitle',
  iconName: 'skippy-filled',
});

const buildDonateToCauseCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.DonateToCause,
  monetaryAsk: true,
  hrefKey: 'my:homepage.overview.donateCta.url',
  titleKey: 'my:homepage.overview.donateCta.title',
  subtitleKey: 'my:homepage.overview.donateCta.subtitle',
  iconName: 'heart-hand',
});

const buildVolunteerCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.Volunteer,
  monetaryAsk: false,
  hrefType: 'url',
  url: `${process.env.NEXT_PUBLIC_BASE_URL_DOTCOM || 'https://www.compassion.com'}${knownSkippyUrls.volunteers}`,
  titleKey: 'my:homepage.overview.volunteerCta.title',
  subtitleKey: 'my:homepage.overview.volunteerCta.subtitle',
  iconName: 'volunteer',
});

const buildAddPaymentMethodCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.AddPaymentMethod,
  monetaryAsk: false,
  hrefKey: 'my:homepage.overview.paymentMethodCta.url',
  titleKey: 'my:homepage.overview.paymentMethodCta.title',
  subtitleKey: 'my:homepage.overview.paymentMethodCta.subtitle',
  iconName: 'wallet-02',
});

const buildSendGiftCta = (childCount: number): SecondaryCtaResult => ({
  type: SecondaryCtaType.SendGift,
  monetaryAsk: true,
  hrefKey: 'my:homepage.overview.giftCta.url',
  titleKey: 'my:homepage.overview.giftCta.title',
  subtitleKey:
    childCount > 1
      ? 'my:homepage.overview.giftCta.subtitleMulti'
      : 'my:homepage.overview.giftCta.subtitle',
  iconName: 'family-gift',
});

const appealDefinitions = [
  {
    type: SecondaryCtaType.GiveThanks,
    metadata: giveThanksAppealMetadata,
  },
  {
    type: SecondaryCtaType.ChristmasGiftCatalog,
    metadata: christmasGiftCatalogAppealMetadata,
  },
] as const;

const buildActiveAppealCtas = (
  now: Date,
  primaryCtas: PrimaryCtaResult[],
): SecondaryCtaResolution =>
  appealDefinitions
    .filter(
      ({ type, metadata }) =>
        isMonthDayWithinRange(
          metadata.startMonthDay,
          metadata.endMonthDay,
          now,
        ) &&
        !(
          type === SecondaryCtaType.ChristmasGiftCatalog &&
          primaryHasChristmas(primaryCtas)
        ),
    )
    .map(
      ({ type, metadata }): SecondaryCtaResult => ({
        type,
        monetaryAsk: true,
        hrefType: 'url',
        url: metadata.href,
        titleKey: metadata.titleTranslationKey,
        subtitleKey: metadata.descriptionTranslationKey,
        iconName: metadata.iconName,
      }),
    );

/**
 * Builds the write-letter CTA. When any writable sponsorship has a stale
 * (>60 day) letter, targets those specific children; otherwise falls back to
 * the generic write-a-letter prompt.
 */
const buildWriteLetterCta = (
  sponsorships: GraphQLCommitment[],
): SecondaryCtaResult => {
  const now = new Date();
  const writable = sponsorships.filter(isSponsorshipCommitmentWritable);
  const stale = writable.filter((c) =>
    isLetterStale(c.lastCorrespondenceDate, now),
  );

  const isSpecific = stale.length > 0;
  const source = isSpecific ? stale : writable;
  const oldestDate = getOldestCorrespondenceDate(source);
  // No date → emit the fallback key; the component translates it.
  const lastWritten = oldestDate
    ? getElapsed(oldestDate) || ''
    : lastWrittenFallbackKey;

  let titleKey: string;
  let interpolation: Record<string, string>;

  if (source.length === 1) {
    titleKey = 'my:homepage.overview.writeCta.title';
    interpolation = {
      childName: source[0].participant?.name ?? '',
      lastWritten,
    };
  } else if (source.length === 2) {
    titleKey = 'my:homepage.overview.writeCta.title_two';
    interpolation = {
      name1: source[0].participant?.name ?? '',
      name2: source[1].participant?.name ?? '',
      lastWritten,
    };
  } else {
    titleKey = 'my:homepage.overview.writeCta.title_other';
    interpolation = { lastWritten };
  }

  return {
    type: isSpecific
      ? SecondaryCtaType.WriteLetterSpecific
      : SecondaryCtaType.WriteLetterGeneric,
    monetaryAsk: false,
    hrefKey: 'my:homepage.overview.writeCta.url',
    titleKey,
    subtitleKey: 'my:homepage.overview.writeCta.subtitle',
    interpolation,
    iconName: 'edit-04',
  };
};

/** Count of active child sponsorships (used for gift CTA copy). */
const getGiftableSponsorshipCount = (
  sponsorships: GraphQLCommitment[],
): number =>
  sponsorships.filter((c) => isSponsorshipCommitmentGiftable(c)).length;

// ─── Resolver ────────────────────────────────────────────────────────────────

/** An active appeal replaces Donate; Volunteer always keeps the second slot. */
const resolveNonSponsorSecondaryCtas = (
  appealCtas: SecondaryCtaResolution,
): SecondaryCtaResolution => [
  appealCtas[0] ?? buildDonateToCauseCta(),
  buildVolunteerCta(),
];

/**
 * Sponsor sub-flow — builds the priority-ordered candidates (Volunteer last as
 * the non-monetary fallback) and lets the budget trim monetary asks.
 */
const resolveSponsorSecondaryCtas = (
  appealCtas: SecondaryCtaResolution,
  limit: number,
  sponsorships: GraphQLCommitment[],
  monetaryBudget: number,
): SecondaryCtaResolution => {
  const candidates: SecondaryCtaResolution = [
    buildWriteLetterCta(sponsorships),
    ...appealCtas,
    buildSendGiftCta(getGiftableSponsorshipCount(sponsorships)),
    buildVolunteerCta(),
  ];

  return selectWithinMonetaryBudget(candidates, limit, monetaryBudget);
};

/**
 * Resolves the secondary CTA(s) for the home page. Pure — reads the current
 * date via the decision functions; no injected clock. i18n keys (including the
 * "last written" fallback) are resolved by the rendering component.
 */
export const resolveSecondaryCtas = ({
  commitments,
  paymentMethods,
  pledges,
  primaryCtas,
}: SecondaryCtaInput): SecondaryCtaResolution => {
  // Apply CTA logic against active sponsorships - exclude sponsorships in transition
  const remainingSponsorships = filterActiveSponsorshipCommitments(commitments);
  const appealCtas = buildActiveAppealCtas(new Date(), primaryCtas);

  // Monetary secondary slots left after the primaries take their share.
  const monetaryBudget = Math.max(
    0,
    MAX_MONETARY_ASKS - countMonetaryAsks(primaryCtas),
  );

  const ctas: SecondaryCtaResolution = [];

  // Tax season → CTA 1 = Tax receipts always show
  // TODO: COULD decide to only show this if there aren't ANY kind of commitments/pledges - but one-time folks could still want it
  if (isTaxSeason()) {
    ctas.push(buildTaxReceiptsCta());
  }

  // Active pledge → Appends to tax CTA, otherwise it's alone if no sponsorships (per MIRO)
  if (hasActivePledge(pledges)) {
    ctas.push(buildBeyondMeasureCta());

    if (remainingSponsorships.length === 0) {
      // If there are no sponsorships, Miro says to show JUST Beyond Measure (and possibly tax)
      return ctas;
    }
  }

  if (ctas.length === 2) {
    return ctas;
  }

  // Any supporter with an active commitment but no payment on file — not just
  // active sponsors.
  if (
    !hasAnyPaymentMethod(paymentMethods) &&
    hasActiveCommitment(commitments)
  ) {
    ctas.push(buildAddPaymentMethodCta());
  }

  if (remainingSponsorships.length === 0) {
    // Not a sponsor → active appeal replaces Donate; Volunteer remains. Only one
    // monetary ask here, so the cap can't be exceeded — no budget check needed.
    ctas.push(...resolveNonSponsorSecondaryCtas(appealCtas));
  } else {
    ctas.push(
      ...resolveSponsorSecondaryCtas(
        appealCtas,
        2 - ctas.length,
        remainingSponsorships,
        monetaryBudget,
      ),
    );
  }

  return ctas.slice(0, 2);
};
